import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { bookCategoryColors, bookCategoryLabels } from '@/constants/learning'
import type { BookBrief } from '@/types'
import { BookOpen, Star } from 'lucide-react'

interface BookCardProps {
  book: BookBrief
  onClick: () => void
}

export function BookCard({ book, onClick }: BookCardProps) {
  return (
    <Card
      className="hover:shadow-lg transition-shadow cursor-pointer overflow-hidden"
      onClick={onClick}
    >
      <div className="flex">
        {/* Book Cover */}
        <div className="w-24 min-h-[140px] bg-muted flex-shrink-0 flex items-center justify-center">
          {book.cover_url ? (
            <img
              src={book.cover_url}
              alt={`Cover of ${book.title}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement
                target.style.display = 'none'
                const parent = target.parentElement
                if (parent) {
                  parent.innerHTML =
                    '<div class="flex items-center justify-center w-full h-full"><svg class="h-8 w-8 text-muted-foreground" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/></svg></div>'
                }
              }}
            />
          ) : (
            <BookOpen className="h-8 w-8 text-muted-foreground" />
          )}
        </div>

        {/* Book Info */}
        <div className="flex-1 min-w-0">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-2">
              <CardTitle className="text-sm leading-tight line-clamp-2">{book.title}</CardTitle>
            </div>
            <p className="text-xs text-muted-foreground">{book.author}</p>
          </CardHeader>
          <CardContent className="pt-0 pb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium ${bookCategoryColors[book.category]}`}
              >
                {bookCategoryLabels[book.category]}
              </span>
              {book.rating && (
                <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                  {book.rating.toFixed(1)}
                </span>
              )}
              {book.page_count && (
                <span className="text-xs text-muted-foreground">{book.page_count}p</span>
              )}
            </div>
          </CardContent>
        </div>
      </div>
    </Card>
  )
}
